import { TextField, Grid, MenuItem, Card, CardHeader, CardContent, Button, CircularProgress, Box } from "@mui/material"
import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { addDays } from "date-fns"

import { useFormik } from "formik"
import * as Yup from "yup"
import { AttachFile } from "@mui/icons-material"
import { saleOrderBusiness } from "businesses/sale-order-business"
import { rootStore } from "stores/root-store"
import { formatDisplayDate } from "utils/date-helper"

const enquiryAboutOptions = ["Item Search", "Call for Return", "Damaged Report", "Delivery Issue", "POD Request", "RTS", "Redirection Request"]

// Form field -> order field, for the read-only fields filled from the reference lookup
const fieldMap = {
  tracking_id: "tracking_num",
  order_lodgement_date: "creation_datetime",
  service_used: "carrier",
  item_contents_description: "item_contents_description",
  street_address: "ship_to_address1",
  address_line_2: "ship_to_address2",
  city: "ship_to_city",
  state: "ship_to_state",
  postal_code: "ship_to_postal_code",
  country: "ship_to_country",
}

const FreightEnquiryInfo = forwardRef(({ detailInfo }, ref) => {
  const [isLookingUp, setIsLookingUp] = useState(false)
  const lastLookedUpReference = useRef((detailInfo?.your_reference || "").trim())

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: detailInfo,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      first_name: Yup.string().max(50).required("First Name is required"),
      last_name: Yup.string().max(50).required("Last Name is required"),
      email: Yup.string().email("Must be a valid email").max(100).required("Email is required"),
      enquiry_about: Yup.string().required("What is your enquiry about? is required"),
      enquiry_description: Yup.string().max(1000).required("Description is required"),
      your_reference: Yup.string().max(100).required("Your Reference (Order Number/Tracking Number) is required"),
    }),
    onSubmit: async values => {},
  })

  useImperativeHandle(ref, () => ({
    async getFormDataIfValid() {
      const val = await formik.validateForm()
      await formik.submitForm()

      if (!val || Object.keys(val).length == 0) return formik.values
      return null
    },
    reset() {
      lastLookedUpReference.current = ""
      formik.resetForm({ values: detailInfo })
    },
  }))

  const handleFileChange = event => {
    const file = event.target.files[0]
    if (file) {
      formik.setFieldValue("attachment", file)
      formik.setFieldValue("attachment_name", file.name)
    }
  }

  const populateFromResponse = async (data, reference) => {
    // The search is a wildcard match across several columns, so "1234" also returns order 12345 and any
    // tracking number containing it. Only fill the read-only fields from an order the reference names exactly.
    const wanted = reference.toUpperCase()
    const same = v => decodeURIComponent(String(v || "")).trim().toUpperCase() === wanted
    const matches = (data || []).filter(row => same(row.ERP_ORDER) || same(row.TRACKING_NUM))
    if (new Set(matches.map(row => row.ERP_ORDER)).size !== 1) return

    const firstResult = matches[0]
    const id = firstResult.ID
    const detailData = await saleOrderBusiness.getDeliveryInformation({
      erp_order: decodeURIComponent(firstResult.ERP_ORDER),
      id,
      archival_search: false,
    })

    if (!detailData) return

    const fullName = detailData.ship_to_name || ""
    const firstName = fullName.length > 0 ? fullName.split(" ")[0] : ""
    const lastName = fullName.length > 0 ? fullName.split(" ").slice(1).join(" ") : ""

    formik.setFieldValue("addressee_first_name", firstName)
    formik.setFieldValue("addressee_last_name", lastName)

    const beforeCurrentDate = formatDisplayDate(addDays(new Date(), -1))

    Object.entries(fieldMap).forEach(([formKey, apiKey]) => {
      let value = formKey === "order_lodgement_date" && detailData[apiKey] ? formatDisplayDate(new Date(detailData[apiKey])) : detailData[apiKey]

      if (formKey === "service_used") {
        value = `${detailData.carrier}${detailData?.carrier_service ? " " + detailData.carrier_service : ""}`
      } else if (formKey === "item_contents_description") {
        value = ""
        if (detailData.details && detailData.details.length > 0) {
          value = detailData.details.map(item => item.product).join(", ")
        }
      } else if (formKey === "country" && value) {
        value = rootStore.getTextByValue("COUNTRY", value)
      } else if (formKey === "order_lodgement_date" && value && value > beforeCurrentDate) {
        value = beforeCurrentDate
      }
      formik.setFieldValue(formKey, value)
    })
  }

  const handleReferenceBlur = async event => {
    formik.handleBlur(event)
    const value = event.target.value?.trim()
    if (!value) return
    if (value === lastLookedUpReference.current) return

    lastLookedUpReference.current = value

    // The read-only fields can't be edited, so a reference that finds nothing must not leave the previous order's details behind
    ;["addressee_first_name", "addressee_last_name", ...Object.keys(fieldMap)].forEach(key => formik.setFieldValue(key, key === "country" ? "Australia" : ""))

    setIsLookingUp(true)
    try {
      let data = await saleOrderBusiness.getData({
        search_term: value,
      })

      await populateFromResponse(data, value)
    } catch {
      /* ignore lookup errors */
    }
    setIsLookingUp(false)
  }

  return (
    <Box sx={{ position: "relative" }}>
      {isLookingUp && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.65)",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Contact Details" />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  id="first_name"
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.first_name}
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  required
                  error={Boolean(formik.touched.first_name && formik.errors.first_name)}
                  helperText={formik.touched.first_name && formik.errors.first_name}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  id="last_name"
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.last_name}
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  required
                  error={Boolean(formik.touched.last_name && formik.errors.last_name)}
                  helperText={formik.touched.last_name && formik.errors.last_name}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  id="email"
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  variant="outlined"
                  inputProps={{ maxLength: 100 }}
                  required
                  error={Boolean(formik.touched.email && formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="What is your enquiry about?"
                  name="enquiry_about"
                  id="enquiry_about"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.enquiry_about}
                  variant="outlined"
                  select
                  required
                  error={Boolean(formik.touched.enquiry_about && formik.errors.enquiry_about)}
                  helperText={formik.touched.enquiry_about && formik.errors.enquiry_about}
                >
                  {enquiryAboutOptions.map(option => (
                    <MenuItem id={`enquiry_about-${option}`} key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Please provide a detailed description of your enquiry"
                  name="enquiry_description"
                  id="enquiry_description"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.enquiry_description}
                  variant="outlined"
                  inputProps={{ maxLength: 1000 }}
                  multiline
                  rows={4}
                  required
                  error={Boolean(formik.touched.enquiry_description && formik.errors.enquiry_description)}
                  helperText={formik.touched.enquiry_description && formik.errors.enquiry_description}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardHeader title="Item Details" />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={12} xs={12}>
                <TextField
                  fullWidth
                  label="Your Reference (Order Number/Tracking Number)"
                  name="your_reference"
                  id="your_reference"
                  onBlur={handleReferenceBlur}
                  onChange={formik.handleChange}
                  value={formik.values.your_reference}
                  variant="outlined"
                  inputProps={{ maxLength: 100 }}
                  required
                  error={Boolean(formik.touched.your_reference && formik.errors.your_reference)}
                  helperText={formik.touched.your_reference && formik.errors.your_reference}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  name="order_lodgement_date"
                  label="Order Lodgement Date"
                  value={formik.values.order_lodgement_date || ""}
                  id="order_lodgement_date"
                  variant="outlined"
                  InputLabelProps={{ shrink: Boolean(formik.values.order_lodgement_date) }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField fullWidth label="Service Used" name="service_used" id="service_used" value={formik.values.service_used} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Item Contents Description"
                  name="item_contents_description"
                  id="item_contents_description"
                  value={formik.values.item_contents_description}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  multiline
                  rows={4}
                />
              </Grid>

              <Grid item xs={12}>
                <Button variant="contained" component="label" startIcon={<AttachFile />}>
                  {formik.values.attachment_name || "Attach a Document or Image"}
                  <input type="file" hidden accept="image/*,.pdf,.doc,.docx" onChange={handleFileChange} />
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="To" />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Addressee First Name"
                  name="addressee_first_name"
                  id="addressee_first_name"
                  value={formik.values.addressee_first_name}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Addressee Last Name"
                  name="addressee_last_name"
                  id="addressee_last_name"
                  value={formik.values.addressee_last_name}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField fullWidth label="Street Address" name="street_address" id="street_address" value={formik.values.street_address} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField fullWidth label="Address Line 2" name="address_line_2" id="address_line_2" value={formik.values.address_line_2} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField fullWidth label="City" name="city" id="city" value={formik.values.city} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField fullWidth label="State / Province / Region" name="state" id="state" value={formik.values.state} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField fullWidth label="Postal / Zip Code" name="postal_code" id="postal_code" value={formik.values.postal_code} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField fullWidth label="Country" name="country" id="country" value={formik.values.country} variant="outlined" InputProps={{ readOnly: true }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    </Box>
  )
})

export default FreightEnquiryInfo
